// src/hooks/useWizard.js
// ─────────────────────────────────────────────────────────────
// All wizard state in one place.
// Components read from this hook; they never manage wizard state locally.
//
// Covers: WZ-01 WZ-02 WZ-03 WZ-04
// ─────────────────────────────────────────────────────────────

import { useState, useCallback } from 'react';
import { track, EVENTS } from '../lib/firebase';

export const WIZARD_STEPS = {
  WELCOME:    0,
  WHO:        1,
  CHALLENGE:  2,
  PROVINCE:   3,
  REFINEMENT: 4,
};

export const TOTAL_STEPS = 4; // steps 1–4 (not counting welcome)

const EMPTY_ANSWERS = {
  who:       [],    // e.g. ['veteran', 'spouse']
  challenge: [],    // e.g. ['mental', 'housing']
  province:  'all',
  refinement: null, // 'no-referral' | 'free' | 'family-access' | 'virtual' | 'any'
};

export function useWizard() {
  const [step, setStep]           = useState(WIZARD_STEPS.WELCOME);
  const [answers, setAnswers]     = useState(EMPTY_ANSWERS);
  const [completed, setCompleted] = useState(false);

  // ── Navigation ─────────────────────────────────────────
  const goToStep = useCallback((n) => {
    setStep(n);
    if (n === WIZARD_STEPS.WHO) track(EVENTS.WIZARD_STARTED);
  }, []);

  const goNext = useCallback(() => {
    setStep(s => Math.min(s + 1, WIZARD_STEPS.REFINEMENT));
  }, []);

  const goBack = useCallback(() => {
    setStep(s => Math.max(s - 1, WIZARD_STEPS.WELCOME));
  }, []);

  // ── Answer setters ──────────────────────────────────────

  // Toggle a value in a multi-select array (who, challenge)
  const toggleMulti = useCallback((field, value) => {
    setAnswers(prev => {
      const arr = prev[field] || [];
      const exists = arr.includes(value);
      return {
        ...prev,
        [field]: exists ? arr.filter(v => v !== value) : [...arr, value],
      };
    });
  }, []);

  const setProvince = useCallback((value) => {
    setAnswers(prev => ({ ...prev, province: value }));
  }, []);

  const setRefinement = useCallback((value) => {
    setAnswers(prev => ({ ...prev, refinement: value }));
  }, []);

  // ── Complete / skip ─────────────────────────────────────
  const completeWizard = useCallback((finalAnswers) => {
    if (finalAnswers) setAnswers(finalAnswers);
    setCompleted(true);
    track(EVENTS.WIZARD_COMPLETED, {
      who:       (finalAnswers || answers).who.join(','),
      challenge: (finalAnswers || answers).challenge.join(','),
      province:  (finalAnswers || answers).province,
    });
  }, [answers]);

  const skipWizard = useCallback(() => {
    setCompleted(true);
    setAnswers(EMPTY_ANSWERS);
    track(EVENTS.WIZARD_SKIPPED, { from_step: step });
  }, [step]);

  // ── Edit answers (WZ-04) ────────────────────────────────
  // Returns user to wizard without losing their current directory context.
  const editAnswers = useCallback(() => {
    setCompleted(false);
    setStep(WIZARD_STEPS.WHO);
  }, []);

  // ── Derived filter values for usePrograms ──────────────
  // Translates wizard answers into the filter shape usePrograms expects.
  const toFilters = useCallback(() => {
    const { who, challenge, province, refinement } = answers;
    return {
      personas:   who,
      challenges: challenge,
      province:   province,
      freeOnly:   refinement === 'free',
      noReferral: refinement === 'no-referral',
      familyOK:   refinement === 'family-access',
    };
  }, [answers]);

  // ── Progress (for progress bar in wizard) ──────────────
  const progress = step === 0 ? 0 : Math.round((step / TOTAL_STEPS) * 100);

  return {
    step,
    answers,
    completed,
    progress,
    goToStep,
    goNext,
    goBack,
    toggleMulti,
    setProvince,
    setRefinement,
    completeWizard,
    skipWizard,
    editAnswers,
    toFilters,
  };
}
